import { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import type { PermissionLevel } from '../shared/api';
import { onBetaAgreementRequired } from '../lib/api';
import { useSession } from '../lib/session';
import { Spinner } from './ui';

/** Where an un-accepted account is sent, carrying where it was headed. */
function agreementPath(returnTo: string): string {
  return `/beta-agreement?returnTo=${encodeURIComponent(returnTo)}`;
}

/**
 * Mounted once, above the routes. Any API call that comes back
 * beta_agreement_required bounces the user to the wall, wherever they were.
 * The guard below catches the common case before a call is even made; this
 * catches the rest — a version bump mid-session, or a call outside a guard.
 */
export function BetaAgreementWatcher() {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(
    () =>
      onBetaAgreementRequired(() => {
        if (location.pathname === '/beta-agreement') return;
        navigate(agreementPath(location.pathname + location.search), { replace: true });
      }),
    [navigate, location.pathname, location.search],
  );
  return null;
}

/**
 * Signed-out users go to sign-in. Field users have no dashboard at all — the
 * phone is their surface — so they get told that rather than a broken portal.
 */
export function RequireAuth({
  children,
  allowUnaccepted = false,
}: {
  children: React.ReactNode;
  /** Set on the agreement wall itself, which must stay reachable un-accepted. */
  allowUnaccepted?: boolean;
}) {
  const { loading, contractor, betaAgreementRequired } = useSession();
  const location = useLocation();
  if (loading) return <Spinner label="Checking your session…" />;
  if (!contractor) {
    const role = location.pathname.startsWith('/properties') || /\/parcels\/[^/]+\/homeowner(?:\/join)?$/.test(location.pathname) ? 'homeowner' : 'contractor';
    return <Navigate to={`/sign-in?role=${role}&returnTo=${encodeURIComponent(location.pathname)}`} replace state={{ from: location.pathname }} />;
  }
  // A contractor field crew lives on the phone. A brokerage field seat is the
  // listing agent (Run 10): their surface is the Files page, not the app wall.
  if (contractor.permissionLevel === 'field' && contractor.companyKind !== 'brokerage') {
    return <Navigate to="/mobile-required" replace />;
  }
  // Early access wall. The backend 403s everything anyway; catching it here
  // means the user never watches a page half-load before being bounced.
  // Reads the backend's verdict, not the timestamp: an invitee whose office has
  // accepted is covered even though their own stub row has signed nothing.
  if (!allowUnaccepted && betaAgreementRequired) {
    return <Navigate to={agreementPath(location.pathname + location.search)} replace />;
  }
  return <>{children}</>;
}

/** Guards a page behind a minimum level; below it, back to the overview. */
export function RequireLevel({ min, children }: { min: PermissionLevel; children: React.ReactNode }) {
  const { loading, can, contractor } = useSession();
  if (loading) return <Spinner />;
  if (!can(min)) {
    const home = contractor?.accountRole === 'homeowner' ? '/properties' : '/dashboard';
    return <Navigate to={home} replace />;
  }
  return <>{children}</>;
}

/** Homeowners cannot open contractor tools; contractors cannot open the property tracker. */
export function RequireRole({
  role,
  children,
}: {
  role: 'homeowner' | 'contractor';
  children: React.ReactNode;
}) {
  const { loading, contractor } = useSession();
  if (loading) return <Spinner />;
  const actual = contractor?.accountRole ?? 'contractor';
  if (actual !== role) {
    return <Navigate to={actual === 'homeowner' ? '/properties' : '/dashboard'} replace />;
  }
  // Run 10: a brokerage field seat (the listing agent) gets Files + Settings —
  // none of the office machinery (jobs, approvals, wallet, team) behind these
  // role-gated routes. /files and /settings sit outside RequireRole.
  if (role === 'contractor' && contractor?.companyKind === 'brokerage' && contractor.permissionLevel === 'field') {
    return <Navigate to="/files" replace />;
  }
  return <>{children}</>;
}
